import React, { useState, useContext } from 'react'
import { Link } from 'react-router-dom'
import styles from './Leaderboard.scss'
import { Completed } from '../ChallengeShowPage'
import { GPTrips } from './GPTrips'
import { OrgContext } from 'features/organizations/OrgContext'
import { rrMessages } from 'features/ride_reports/intlMessages'
import { friendsMessages } from 'features/homepage/Feed'
import cardStyles from 'common/SearchableCard/SearchableCard.scss'
import { CircledIcon } from 'common/CircledIcon'
import { Icon } from 'common/Icon'
import { TabCard, TabCardItem } from 'common/styleguide/TabCard'
import { CTA } from 'common/styleguide/CTA'
import { SectionBox } from 'common/styleguide/SectionBox'
import { SHARE_MODAL } from 'common/modals/ShareModal'
import { CircleSpinner } from 'common/spinners/CircleSpinner'
import { Msg, commonCTAs, commonMessages, goalTypeLabels } from 'lib/localization'
import { showModal } from 'lib/modalUtils'
import { urlWithPrivacyCode } from 'lib/utils'
import { urls } from 'lib/urls'
import { Set } from 'immutable'
import { defineMessages } from 'react-intl'
import cx from 'classnames'

const LEADERBOARD_PER_PAGE = 25
const SPLIT_INDEX = 9

export const messages = defineMessages({
  adminEmpty: {
    id: 'Leaderboard.adminEmpty',
    defaultMessage: 'Who’s in the lead? Who has ground to make up? The Leaderboard will show where your participants stand in the Challenge.'
  },
  editLeaderboard: {
    id: 'Leaderboard.editLeaderboard',
    defaultMessage: 'Edit Leaderboard'
  },
  emptyLeaderboard: {
    id: 'Leaderboard.emptyLeaderboard',
    defaultMessage: 'The Leaderboard will show where you and other participants stand once the Challenge begins.'
  },
  emptyFollowing: {
    id: 'Leaderboard.emptyFollowing',
    defaultMessage: 'Invite friends to join by sharing the Challenge link.'
  },
  emptyFollowingPrivate: {
    id: 'Leaderboard.emptyFollowingPrivate',
    defaultMessage: 'Participants you are following will show up here.'
  },
  viewFullLeaderboard: {
    id: 'Leaderboard.viewFullLeaderboard',
    defaultMessage: 'View Full Leaderboard'
  },
  activities: {
    id: 'Leaderboard.activities',
    defaultMessage: '{count, plural, =0 {activities} one {activity} other {activities}}'
  }
})

export const Leaderboard = ({ follows, followedParticipants, ...props }) => {
  const { fm } = props
  const { isCurrentClub } = useContext(OrgContext)
  const [selected, setSelected] = useState('leaderboard')
  const [pagination, setPagination] = useState({
    loading: false,
    page: 0
  })

  const commonProps = { fm, selected, pagination, setPagination, ...props }

  const onChange = tab => {
    tab === 'following' && props.currentUser.id && props.loadParticipants({ following: true })
    setSelected(tab)
  }

  if (isCurrentClub && !props.preview) {
    return <SectionBox noPadding title={fm(goalTypeLabels.leaderboard)} rightTitle={props.rightTitle}>
      <AdminLeaderboardBody {...commonProps} />
    </SectionBox>
  }

  return (
    <TabCard selectedTab={selected} onChange={onChange} ALLOW_UNSAFE_TAB_WIDTH>
      <TabCardItem id='leaderboard' title={fm(goalTypeLabels.leaderboard)}>
        <LeaderboardBody {...commonProps} />
      </TabCardItem>
      <TabCardItem id='following' title={fm(rrMessages.following)}>
        <LeaderboardBody follows={follows} followedParticipants={followedParticipants} {...commonProps} />
      </TabCardItem>
    </TabCard>
  )
}

const AdminLeaderboardBody = props => {
  if (props.participants?.size === 0) {
    const { goal, fm } = props
    return <EmptyBody
      i='md-trophy'
      message={fm(messages.adminEmpty)}
      to={!props.noLink && `/challenges/${goal.id}/leaderboard`}
      link={!props.noLink && fm(messages.editLeaderboard)} />
  }

  return <LeaderboardBody {...props} />
}

const LeaderboardBody = ({
  goal,
  metaForGoal,
  participant,
  participants,
  loadParticipants,
  currentUserParticipant,
  currentUser,
  cuRowRef,
  followedParticipants,
  follows,
  ranks,
  condensed,
  setCondensed,
  selected,
  pagination,
  setPagination,
  hideLoadMore,
  collapsed,
  fm
}) => {
  const [expanded, setExpanded] = useState(Set())
  if (selected === 'leaderboard' ? ranks.gpRanks.length === 0 : followedParticipants(follows).size === 0) {
    return <UserEmptyBody goal={goal} selected={selected} fm={fm} />
  }

  const { goalType } = goal
  const { gpRanks } = ranks
  const cuRank = currentUserParticipant?.rank
  const currentUserId = currentUser?.id
  const gpProps = gp => ({
    key: gp.id,
    expanded: expanded.has(gp.id),
    toggle: e => setExpanded(expanded.has(gp.id) ? expanded.delete(gp.id) : expanded.add(gp.id)),
    isUser: gp.userId === currentUserId,
    goalType: goalType,
    goal,
    cuRowRef,
    p: gp,
    collapsed,
    fm
  })

  if (!follows && condensed && cuRank > LEADERBOARD_PER_PAGE) {
    // render split view
    return <div className={styles.table}>
      {gpRanks.slice(0, SPLIT_INDEX + 1).map(participant).map(gp => {
        return <GPRow {...gpProps(gp)} />
      })}
      <div className={styles.splitRow}>
        <Icon i='ios-more' size={36} />
      </div>
      {gpRanks.slice(cuRank - 3, cuRank + 8).map(participant).filter(gp => gp).map(gp => {
        return <GPRow {...gpProps(gp)} />
      })}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <CTA autoWidth onClick={() => setCondensed(false)} style={{ margin: '30px 0 20px 0' }} secondary>{fm(messages.viewFullLeaderboard)}</CTA>
      </div>
    </div>
  }

  const loadMore = () => {
    setPagination({
      ...pagination,
      loading: true
    })
    const page = pagination.page + 1
    loadParticipants({ page })
      .then(() => {
        setPagination({
          loading: false,
          page
        })
      })
  }

  const nShown = (pagination.page + 1) * LEADERBOARD_PER_PAGE

  if (selected === 'leaderboard') {
    return <div className={styles.table}>
      {gpRanks.slice(0, nShown).map(participant).filter(p => p).map(gp => <GPRow {...gpProps(gp)} />)}
      {hideLoadMore
        ? <div style={{ display: 'flex', justifyContent: 'center', margin: '30px 0px 20px 0px' }}>
          <CTA
            secondary
            autoWidth
            margins={false}
            to={`/challenges/${goal.get('id')}/leaderboard`}>
            {fm(messages.editLeaderboard)}
          </CTA>
        </div>
        : <>
          {nShown < gpRanks.length &&
        gpRanks.length >= participants.size &&
        <div className={cardStyles.resultsFooter}>
          <CTA inline secondary margins={false} disabled={pagination.loading} onClick={() => !pagination.loading && loadMore()}>
            {pagination.loading && <CircleSpinner size={14} />} <Msg m={commonCTAs.loadMore} />
          </CTA>
          <div style={{ paddingTop: '20px' }}>
            <span className={cardStyles.footerText}>
              <Msg m={commonMessages.showingResults} values={{ length: nShown, totalCount: gpRanks.length }} />
            </span>
          </div>
        </div>}
          {!pagination.loading && gpRanks.length > LEADERBOARD_PER_PAGE &&
        nShown >= gpRanks.length &&
        participants.size === gpRanks.length &&
        <div className={cardStyles.resultsFooter}>
          <span className={cardStyles.footerText}><Msg m={commonMessages.noMoreItems} /></span>
        </div>}
        </>}
    </div>
  }

  const followedUserIds = follows.map(({ itemType, itemId }) => itemType === 'User' && itemId).valueSeq().toSet()
  const followedParticipantIds = participants.filter(p => p.userId === currentUserId || followedUserIds.has(p.userId))
  const followedGpRanks = gpRanks.map(pId => followedParticipantIds.has(pId) && participant(pId)).filter(p => p)

  const isEmpty = currentUserParticipant
    ? followedParticipantIds.size - 1 === 0 // -1 here because followedParticipantIds includes the participant for the currentUser
    : followedGpRanks.length === 0

  return <div className={styles.table}>
    {!isEmpty
      ? followedGpRanks.map(gp => <GPRow {...gpProps(gp)} />)
      : <UserEmptyBody goal={goal} selected={selected} fm={fm} /> }
  </div>
}

const GPRow = ({ isUser, cuRowRef, goalType, goal, p, expanded, toggle, collapsed, fm }) => {
  const rest = <>
    <div className={styles.td} style={{ width: '40px' }}>
      <img height='30' width='30' src={p.userProfileImg > 0
        ? urls.photos.milli(p.userProfileImg)
        : urls.users.profileDefault.small(p.userId)} />
    </div>
    <div className={cx(styles.td, styles.grow)} style={collapsed ? { width: 'calc(100% - 85px)', marginBottom: '5px' } : undefined}>
      <Link to={`/users/${p.userId}`}>{p.userName}</Link>
    </div>
    <div className={styles.td}>
      {isUser && !collapsed && <Icon i='md-trophy' className={styles.userTrophy} />}
    </div>
    <div className={cx(styles.td, collapsed ? styles.leftAlign : styles.rightAlign)} style={{ width: '100px' }}>
      <Completed goalType={goalType} value={p.amountCompleted} fm={fm} noLabel />
    </div>
    {goal.get('goalParams')['has_target'] && <div className={cx(styles.td, styles.rightAlign)} style={{ width: collapsed ? '50px' : '80px' }}>{p.goalParams.progress_text}</div> }
    <div className={cx(styles.td, styles.rightAlign)} style={{ width: '120px' }}>{p.trips} {fm(messages.activities, { count: p.trips })}</div>
    {!collapsed && <div onClick={toggle} className={cx(styles.td, styles.rightAlign)} style={{ width: '35px' }}>
      <Icon i='ios-arrow-down' size={22} className={cx(styles.expandIcon, expanded && styles.expanded)} />
    </div>}
  </>

  return <>
    <div className={cx(styles.gpRow, expanded && styles.expanded, collapsed && styles.collapsed, isUser && styles.isUser)} ref={isUser ? cuRowRef : undefined}>
      <div className={cx(styles.td, collapsed && styles.collapsed)} style={{ width: '45px' }}>{p.rank}</div>
      {collapsed
        ? <div className={styles.rowWrapper}>
          {rest}
        </div>
        : rest}
    </div>
    {expanded && <GPTrips goal={goal} p={p} isUser={isUser} />}
  </>
}

const UserEmptyBody = ({ goal, selected, fm }) => <EmptyBody
  i='invite-friends-icon'
  message={fm(selected === 'leaderboard'
    ? messages.emptyLeaderboard
    : goal.visibility === 0 ? messages.emptyFollowing : messages.emptyFollowingPrivate)}
  onClick={() => {
    goal.visibility === 0
      ? showModal(SHARE_MODAL, {
        objectId: goal.id,
        objectType: 'goal',
        objectTitle: goal.name,
        objectUrl: urlWithPrivacyCode(`/challenges/${goal.id}`, goal.privacyCode)
      })
      : window.location = '/users'
  }}
  link={fm(goal.visibility === 0 ? friendsMessages.inviteFriends : friendsMessages.findFriends)}
/>

const EmptyBody = ({ i, message, to, onClick, link }) => {
  return <div className={styles.emptyBody}>
    <CircledIcon i={i} size={60} />
    {message}
    {(to || onClick) && link && <CTA secondary to={to} onClick={onClick}>{link}</CTA>}
  </div>
}
